import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { generateProposal, uploadTranscript, listTemplates } from "@/lib/api";
import { Upload, Wand2, ArrowLeft, FileText, LayoutTemplate, Check } from "lucide-react";
import { toast } from "sonner";
import { useComingSoon } from "@/components/ComingSoon";

const SAMPLE = `Sales Rep (Alex): Hey Maria, thanks for joining today.
Maria Chen (VP Ops, Northwind Logistics): Of course. We've been drowning in manual freight tracking.
Alex: Tell me more about the current setup.
Maria: 240-person ops team across Seattle and Austin. We use a mix of TMS, spreadsheets, and Slack to coordinate. Industry-wide visibility is broken. We want a single pane of glass.
Alex: Got it. What's your timeline and budget?
Maria: We'd want to pilot in Q2, full roll-out by EOY. Budget is around $180k ARR. Procurement requires SOC2 Type II.
Alex: We have SOC2 Type II. Integrations needed?
Maria: Workday, NetSuite, and our internal driver app.
Alex: ROI metrics?
Maria: Reduce dwell time 25%, save ops 12 hours/week, fewer billing disputes.
Alex: Competitors evaluated?
Maria: FourKites and Project44. They felt heavy. CEO wants velocity.
Alex: Concerns?
Maria: Change management. And we want migration support.`;

const STEPS = [
  "Reading transcript",
  "Extracting company & contacts",
  "Identifying pain points",
  "Inferring budget & timeline",
  "Drafting proposal sections",
  "Polishing tone & structure",
];

export default function NewProposal() {
  const nav = useNavigate();
  const cs = useComingSoon();
  const [text, setText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);

  useEffect(() => {
    listTemplates().then((ts) => {
      setTemplates(ts);
      const def = ts.find((t) => t.is_default) || ts[0];
      if (def) setTemplateId(def.id);
    }).catch(() => {});
  }, []);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const fileRef = useRef(null);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const { text: t } = await uploadTranscript(f);
      setText(t || "");
      toast.success(`${f.name} loaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read file");
    } finally {
      e.target.value = "";
    }
  };

  const onGenerate = async () => {
    if (text.trim().length < 30) {
      toast.error("Transcript looks too short");
      return;
    }
    setBusy(true);
    setStep(0);
    setElapsed(0);
    const stepInterval = setInterval(
      () => setStep((s) => (s + 1) % STEPS.length),
      1800,
    );
    const clockInterval = setInterval(() => setElapsed((e) => e + 1), 1000);
    try {
      const p = await generateProposal(text, templateId);
      clearInterval(stepInterval);
      clearInterval(clockInterval);
      toast.success("Proposal generated");
      nav(`/proposals/${p.id}`);
    } catch (e) {
      clearInterval(stepInterval);
      clearInterval(clockInterval);
      setBusy(false);
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Generation failed. Try again.",
      );
    }
  };

  if (busy) {
    return (
      <div className="min-h-[80vh] grid place-items-center px-6">
        <div className="max-w-md w-full text-center fade-in">
          <div className="label-eyebrow mb-3">Working · {elapsed}s</div>
          <h2 className="font-display text-3xl ai-shimmer mb-6">Analyzing conversation…</h2>
          <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-left editor-shell">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3 py-2">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    i === step ? "bg-[#011B90] animate-pulse" : "bg-[#E8EAF3]"
                  }`}
                />
                <span className={`text-sm ${i === step ? "text-zinc-900" : "text-zinc-400"}`}>{s}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-4">
            GPT-5.2 usually takes 30–60 seconds. Please don't close this tab.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 fade-in">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> Back
      </button>
      <div className="label-eyebrow mb-3">Step 1 of 4</div>
      <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight">New Proposal</h1>
      <p className="text-sm text-zinc-500 mt-2 max-w-lg">
        Paste a sales call transcript. We'll extract the deal context and draft a complete, editable proposal.
      </p>

      <div className="mt-8 quo-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <LayoutTemplate size={14} className="text-[#011B90]" />
          <div className="text-[13px] font-medium text-[#0B1736]">Template</div>
          <span className="text-[11px] text-[#9098A8]">Choose a starting layout — AI generates each section per template.</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {templates.map((t) => {
            const active = t.id === templateId;
            const badge = { ae: "AE", csm: "CSM", prof_serv: "Prof. Services", mom: "Recap", upsell: "Upsell", consulting: "Consulting", quick_quote: "Quick Quote", both: "Both" }[t.role] || "Both";
            const isOnePager = t.layout === "onepager";
            return (
              <button
                key={t.id}
                data-testid={`template-${t.id}`}
                onClick={() => setTemplateId(t.id)}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  active
                    ? "border-[#011B90] bg-[#EDEFF7] shadow-[0_8px_24px_-14px_rgba(1, 27, 144,0.55)]"
                    : "border-[#E8EAF3] bg-white hover:border-[#011B90]/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[13px] font-medium text-[#0B1736]">{t.name}</div>
                  {active && <Check size={14} className="text-[#011B90]" />}
                </div>
                <div className="text-[11px] text-[#9098A8] mt-0.5">{t.description}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] tracking-widest uppercase font-semibold text-[#011B90]">{badge}</span>
                  <span className="text-[10px] text-[#9098A8]">{t.section_titles.length} sections</span>
                  {isOnePager && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">One-pager</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-[#E8EAF3] bg-white editor-shell">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div className="flex items-center gap-2 text-zinc-600 text-xs">
            <FileText size={14} />
            <span>Transcript</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="sample-btn"
              onClick={() => setText(SAMPLE)}
              className="text-xs text-zinc-600 hover:text-zinc-900"
            >
              Use sample
            </button>
            <input ref={fileRef} type="file" accept=".txt,.docx" className="hidden" onChange={onFile} data-testid="file-input" />
            <button
              data-testid="upload-btn"
              onClick={() => fileRef.current?.click()}
              className="text-xs text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
            >
              <Upload size={14} /> Upload .txt / .docx
            </button>
          </div>
        </div>
        <Textarea
          data-testid="transcript-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Paste your sales call transcript here…"
          className="min-h-[340px] border-0 focus-visible:ring-0 resize-y bg-white text-[15px] leading-relaxed p-5"
        />
      </div>

      <div className="flex items-center justify-between mt-5">
        <div className="text-xs text-zinc-500">
          {text.length.toLocaleString()} characters
          {text.length > 45000 && (
            <span className="ml-2 text-[#011B90]">· Long transcript detected — we'll condense key points before generating.</span>
          )}
        </div>
        <Button
          data-testid="generate-btn"
          onClick={onGenerate}
          className="bg-[#011B90] hover:bg-[#011670] text-white rounded-md h-11 px-5 shadow-[0_8px_24px_-8px_rgba(1, 27, 144,0.55)]"
        >
          <Wand2 size={15} className="mr-2" /> Generate Proposal
        </Button>
      </div>

      <div className="mt-10 grid grid-cols-3 gap-3 text-[11px] text-[#5F6677]">
        {["Fireflies", "Granola", "Gong", "Fathom", "Zoom", "Otter"].map((s) => (
          <button
            key={s}
            data-testid={`connector-${s.toLowerCase()}`}
            onClick={() => cs(`${s} import`, `Auto-import transcripts from ${s} is coming soon. For now, paste the transcript above or upload a .txt/.docx file.`)}
            className="rounded-md border border-dashed border-[#E8EAF3] px-3 py-2 text-center hover:border-[#011B90] hover:text-[#0B1736] hover:bg-[#EDEFF7] transition-colors"
          >
            {s} <span className="text-[#9098A8]">— coming soon</span>
          </button>
        ))}
      </div>
    </div>
  );
}
